import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CategoryService } from '../../osm-services/category.service';
import { UserFavAppService, UserFavApp } from '../../osm-services/user-fav-app.service';
import { AuthService } from '../../services/auth.service';
import { SubCategory } from '../../models/sub-category';
import { ImageUtils } from '../../shared/utils/image.utils';
import { CategoryUtils } from '../../shared/utils/category.utils';
import { EnhancedApp, enhanceApp } from '../../shared/interfaces/enhanced-app.interface';
import { AppInCategory, CategoryWithApps } from '../../models/category.model';

declare var jQuery: any;

@Component({
  selector: 'ozm-category',
  templateUrl: './category.component.html',
  styleUrls: ['./category.component.scss']
})
export class CategoryComponent implements OnInit {
  category: string = '';
  selectedCategoryId: string = '';
  categories: CategoryWithApps[] = [];
  subCategories: SubCategory[] = [];
  selectedSubcategory: string = ''; // Selected subcategory name from URL
  subcategores_data: any;
  
  // Subcategory filter (from URL path params, not query params anymore)
  filterSubCategoryId: string = '';
  filterSubCategoryName: string = '';
  
  // Apps in selected category
  appsInSelectedCategory: AppInCategory[] = [];
  allAppsInCategory: AppInCategory[] = []; // Store all apps before filtering
  selectedApps: { [key: string]: boolean } = {};
  
  // User's favorite apps
  userFavoriteApps: UserFavApp[] = [];
  
  // Loading states
  loading: boolean = false;
  favoritesLoading: boolean = false;
  subcategoriesLoading: boolean = false;
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private categoryService: CategoryService,
    private userFavAppService: UserFavAppService,
    private authService: AuthService
  ) {}

  // Utility methods for consistent UI
  getAppIcon(app: any): string {
    return ImageUtils.getAppIcon(app);
  }

  handleImageError(event: Event): void {
    ImageUtils.handleImageError(event);
  }

  getDisplayCategoryName(app: any): string {
    return CategoryUtils.getCategoryDisplayName(app.category, []);
  }

  getDisplaySubcategoryName(app: any): string {
    // Convert SubCategory[] to SubcategoryInfo[] format
    const subcategoryInfos = this.subCategories.map(sub => ({
      subCategoryId: sub.subCategoryId,
      subCategoryName: sub.name
    }));
    return CategoryUtils.getSubcategoryDisplayName(app.subCategory, subcategoryInfos);
  }

  ngOnInit(): void {
    // Load user's favorite apps
    this.loadUserFavoriteApps();
    
    // Subscribe to route parameters
    this.route.params.subscribe(params => {
      console.log('Route params changed:', params);
      
      // Get category name from URL
      if (params['name']) {
        this.category = params['name'];
        console.log('Category set from route:', this.category);
        
        // Get subcategory from URL path (not query params)
        this.selectedSubcategory = params['subcategory'] || '';
        this.filterSubCategoryName = this.selectedSubcategory;
        console.log('Subcategory from URL:', this.selectedSubcategory);
        
        // If categories are already loaded, load category apps immediately
        if (this.categories.length > 0) {
          this.loadCategoryApps();
          this.loadSubcategoriesForCategory();
        }
      } else {
        this.category = '';
        this.selectedSubcategory = '';
      }
    });
    
    // Load all categories with apps
    this.loadCategories();

    // Initialize slider after view is ready
    setTimeout(() => {
      this.initializeSlider();
    }, 100);
  }

  // Initialize slider using native JavaScript instead of jQuery
  private initializeSlider(): void {
    try {
      // Check if jQuery and bxSlider are available
      if (typeof jQuery !== 'undefined' && jQuery.fn.bxSlider) {
        jQuery(".offer-slider-ul").bxSlider({
          minSlides: 3,
          maxSlides: 3,
          moveSlides: 1,
          slideWidth: 350,
          slideMargin: 35,
          touchEnabled: false,
          pager: false,
        });
      } else {
        console.log('jQuery or bxSlider not available, skipping slider initialization');
      }
    } catch (error) {
      console.warn('Failed to initialize slider:', error);
    }
  }

  // Load all categories with apps
  loadCategories(): void {
    this.loading = true;
    console.log('Loading categories...');
    
    this.categoryService.getCategoriesWithApps().subscribe({
      next: (response) => {
        console.log('Categories API response:', response);
        
        // Handle the actual API response format
        if (response && response.Items) {
          this.categories = response.Items;
        } else if (response && response.items) {
          this.categories = response.items;
        } else if (Array.isArray(response)) {
          this.categories = response;
        } else if (response && response.result) {
          this.categories = response.result;
        } else {
          this.categories = [];
        }
        
        // Process apps to ensure they have appliId
        this.categories.forEach(category => {
          if (category.apps) {
            category.apps.forEach((app, index) => {
              // If appliId doesn't exist, create one from name or use index
              if (!app.appliId) {
                app.appliId = app.name ? app.name.toLowerCase().replace(/\s+/g, '-') + '-' + index : 'app-' + index;
              }
            });
          }
        });
        
        console.log('Processed categories:', this.categories);
        this.loading = false;
        
        // If category is already set from route, load its apps and subcategories
        if (this.category) {
          console.log('Loading apps for category:', this.category);
          this.loadCategoryApps();
          this.loadSubcategoriesForCategory();
        }
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.loading = false;
      }
    });
  }
  
  // Load subcategories for the selected category
  loadSubcategoriesForCategory(): void {
    if (!this.selectedCategoryId) {
      const categoryObj = this.categories.find(c => c.name === this.category);
      if (categoryObj) {
        this.selectedCategoryId = categoryObj.categoryId;
      }
    }
    
    if (!this.selectedCategoryId) {
      console.log('No category ID available, cannot load subcategories');
      return;
    }
    
    this.subcategoriesLoading = true;
    console.log('Loading subcategories for category:', this.selectedCategoryId);
    
    this.categoryService.getCategoryWithSubcategories(this.selectedCategoryId).subscribe({
      next: (response) => {
        console.log('Subcategories loaded:', response);
        this.subCategories = response.subcategories || [];
        
        // If a subcategory is selected from URL, find its ID
        if (this.selectedSubcategory && !this.filterSubCategoryId) {
          const subcat = this.subCategories.find(s => s.name === this.selectedSubcategory);
          if (subcat) {
            this.filterSubCategoryId = subcat.subCategoryId;
            console.log('Found subcategory ID:', this.filterSubCategoryId);
          }
        }
        
        this.subcategoriesLoading = false;
        
        // Reapply filter with the found subcategory ID
        if (this.allAppsInCategory.length > 0) {
          this.applySubcategoryFilter();
        }
      },
      error: (error) => {
        console.error('Error loading subcategories:', error);
        this.subcategoriesLoading = false;
      }
    });
  }
  
  // Load user's favorite apps (OLD API - deprecated, kept for backward compatibility)
  loadUserFavoriteApps(): void {
    console.log('Loading user favorite apps... (using old bulk API)');
    
    // NOTE: This uses the old /user_fav_app API which has auth issues
    // We now use the new per-subcategory API instead
    // Keeping this for backward compatibility but making it non-blocking
    
    this.userFavAppService.getUserFavApps().subscribe({
      next: (response) => {
        console.log('✓ Favorite apps loaded from old API:', response);
        this.userFavoriteApps = response.apps || [];
        
        // Update favorite status for apps in current category
        this.updateFavoriteStatus();
      },
      error: (error) => {
        console.warn('⚠️ Old favorites API failed (this is expected):', error.status, error.statusText);
        console.warn('⚠️ Using new per-subcategory favorites API instead');
        // Don't show error to user - this is expected
        // The new API will load favorites per subcategory
        this.userFavoriteApps = [];
      }
    });
  }
  
  // Update favorite status for apps in current category
  private updateFavoriteStatus(): void {
    if (this.appsInSelectedCategory.length > 0) {
      const favAppIds = this.userFavoriteApps.map(app => app.appliId);
      this.appsInSelectedCategory.forEach(app => {
        app.isFavorite = favAppIds.includes(app.appliId);
      });
    }
  }
  
  // Load apps for selected category
  loadCategoryApps(): void {
    console.log('=== LOAD CATEGORY APPS ===');
    console.log('Loading apps for category:', this.category);
    console.log('Available categories:', this.categories.map(c => c.name));
    
    const categoryObj = this.categories.find(c => c.name === this.category);
    console.log('Found category object:', categoryObj ? categoryObj.name : 'NOT FOUND');
    
    if (categoryObj) {
      this.selectedCategoryId = categoryObj.categoryId;
      this.allAppsInCategory = categoryObj.apps || [];
      console.log('Category ID:', this.selectedCategoryId);
      console.log('Total apps in category:', this.allAppsInCategory.length);
      console.log('First 5 apps:', this.allAppsInCategory.slice(0, 5).map(app => ({
        name: app.name,
        category: app.category,
        subCategory: app.subCategory
      })));
      
      // Apply subcategory filter if specified
      this.applySubcategoryFilter();
      
      // Check which apps are already in favorites
      this.checkFavoriteApps();
    } else {
      console.log('Category not found, clearing apps');
      this.allAppsInCategory = [];
      this.appsInSelectedCategory = [];
    }
  }
  
  // Apply subcategory filter to apps
  applySubcategoryFilter(): void {
    console.log('=== APPLY SUBCATEGORY FILTER ===');
    console.log('Filter params:', {
      filterSubCategoryId: this.filterSubCategoryId,
      filterSubCategoryName: this.filterSubCategoryName,
      selectedSubcategory: this.selectedSubcategory,
      category: this.category
    });
    console.log('Total apps before filter:', this.allAppsInCategory.length);
    
    // Log ALL apps to see what we have
    console.log('ALL APPS IN CATEGORY:');
    this.allAppsInCategory.forEach((app, index) => {
      console.log(`${index + 1}. "${app.name}" - category: "${app.category}", subCategory: "${app.subCategory}"`);
    });
    
    const subcategoryToFilter = this.filterSubCategoryName || this.selectedSubcategory;
    
    if (subcategoryToFilter) {
      console.log('Filtering by subcategory:', subcategoryToFilter);
      console.log('Subcategory ID to match:', this.filterSubCategoryId);
      
      // Check what unique subcategory values exist in the apps
      const uniqueSubCategories = [...new Set(this.allAppsInCategory.map(app => app.subCategory))];
      console.log('⚠️ AVAILABLE SUBCATEGORIES IN APPS:', uniqueSubCategories);
      console.log('⚠️ LOOKING FOR SUBCATEGORY NAME:', subcategoryToFilter);
      console.log('⚠️ LOOKING FOR SUBCATEGORY ID:', this.filterSubCategoryId);
      
      // Filter by subcategory - PRIORITIZE UUID matching over name matching
      this.appsInSelectedCategory = this.allAppsInCategory.filter(app => {
        const appSubCategory = (app.subCategory || '').trim();
        const filterSubCategory = subcategoryToFilter.trim();
        const filterId = this.filterSubCategoryId || '';
        
        // STRATEGY 1: Match by UUID (MOST RELIABLE)
        // If we have a filterSubCategoryId, try to match it against app.subCategory
        const idMatch = filterId && appSubCategory === filterId;
        
        if (idMatch) {
          console.log(`✓ MATCH (UUID): "${app.name}" - subCategory: "${appSubCategory}" matches ID: "${filterId}"`);
          return true;
        }
        
        // STRATEGY 2: Exact name match (case-insensitive) - for backward compatibility
        const exactMatch = appSubCategory.toLowerCase() === filterSubCategory.toLowerCase();
        
        if (exactMatch) {
          console.log(`✓ MATCH (exact name): "${app.name}" - subCategory: "${appSubCategory}"`);
          return true;
        }
        
        // STRATEGY 3: Partial match (contains) - fallback
        const partialMatch = appSubCategory.toLowerCase().includes(filterSubCategory.toLowerCase()) ||
                            filterSubCategory.toLowerCase().includes(appSubCategory.toLowerCase());
        
        if (partialMatch) {
          console.log(`✓ MATCH (partial): "${app.name}" - subCategory: "${appSubCategory}"`);
          return true;
        }
        
        return false;
      });
      
      console.log('=== FILTER RESULT ===');
      console.log('Filtered apps count:', this.appsInSelectedCategory.length);
      console.log('Filtered apps:', this.appsInSelectedCategory.map(app => `${app.name} (${app.subCategory})`));
      
      if (this.appsInSelectedCategory.length === 0) {
        console.warn('⚠️ DATA MISMATCH: No apps found with subCategory matching "' + subcategoryToFilter + '"');
        console.warn('⚠️ Apps in this category use these subcategory values:', uniqueSubCategories);
        console.warn('⚠️ You need to either:');
        console.warn('   1. Update MongoDB apps to use subCategory: "' + subcategoryToFilter + '"');
        console.warn('   2. Change the subcategory name in MongoDB to match app values');
      }
    } else {
      // No filter, show all apps in category
      this.appsInSelectedCategory = [...this.allAppsInCategory];
      console.log('No filter, showing all', this.appsInSelectedCategory.length, 'apps in category');
    }
  }
  
  // Clear subcategory filter
  clearSubcategoryFilter(): void {
    this.selectedSubcategory = '';
    this.filterSubCategoryId = '';
    this.filterSubCategoryName = '';
    
    // Navigate to category page without subcategory
    this.router.navigate(['/category', this.category]);
  }
  
  // Select a subcategory
  selectSubcategory(subcategory: SubCategory): void {
    console.log('Subcategory selected:', subcategory);
    this.selectedSubcategory = subcategory.name;
    this.filterSubCategoryName = subcategory.name;
    this.filterSubCategoryId = subcategory.subCategoryId || '';
    
    // Navigate to category/subcategory URL
    this.router.navigate(['/category', this.category, subcategory.name]);
  }
  
  // Select/deselect an app
  toggleAppSelection(app: AppInCategory): void {
    if (this.selectedApps[app.appliId]) {
      this.selectedApps[app.appliId] = false;
    } else {
      this.selectedApps[app.appliId] = true;
    }
  }
  
  // Check if app is selected
  isAppSelected(appId: string): boolean {
    return this.selectedApps[appId] === true;
  }
  
  // Get IDs of selected apps
  getSelectedAppIds(): string[] {
    return Object.keys(this.selectedApps).filter(id => this.selectedApps[id]);
  }
  
  // Get app by ID
  getAppById(appId: string): AppInCategory | undefined {
    return this.appsInSelectedCategory.find(app => app.appliId === appId);
  }
  
  // Add selected apps to favorites
  addSelectedAppsToFavorites(): void {
    console.log('Button clicked - addSelectedAppsToFavorites method called');
    
    const selectedAppIds = this.getSelectedAppIds();
    console.log('Selected app IDs:', selectedAppIds);
    
    if (selectedAppIds.length === 0) {
      alert('Please select at least one app to add to favorites');
      return;
    }
    
    this.favoritesLoading = true;
    console.log('Adding apps to favorites...');
    
    // Get user ID
    this.userFavAppService['authService'].getUser().then(user => {
      if (!user || !user.attributes || !user.attributes.sub) {
        alert('User not authenticated');
        this.favoritesLoading = false;
        return;
      }
      
      const userId = user.attributes.sub;
      
      // If filtering by subcategory, add multiple apps to that subcategory
      if (this.selectedSubcategory && this.filterSubCategoryId) {
        const selectedApps = selectedAppIds.map(id => this.getAppById(id)).filter(app => app !== undefined);
        
        if (selectedApps.length === 0) {
          alert('Selected apps not found');
          this.favoritesLoading = false;
          return;
        }
        
        // Add all selected apps to the subcategory
        const favoriteDataArray = selectedApps.map(app => ({
          subCategoryId: this.filterSubCategoryId,
          subCategoryName: this.selectedSubcategory,
          appliId: app!.appliId,
          appName: app!.name,
          icon: app!.icon || '',
          link: app!.link || '',
          androidImageUrl: app!.androidImageUrl || ''
        }));
        
        console.log('Adding multiple favorites for subcategory:', favoriteDataArray);
        
        // Call backend to add multiple apps to subcategory
        this.userFavAppService.setMultipleFavoriteApps(userId, favoriteDataArray).subscribe({
          next: (response) => {
            console.log('Successfully added favorites:', response);
            
            // Update the UI immediately
            selectedApps.forEach(app => {
              if (app) app.isFavorite = true;
            });
            
            // Clear selections
            this.selectedApps = {};
            
            // Refresh favorite apps list
            this.loadUserFavoriteApps();
            
            this.favoritesLoading = false;
            alert(`Successfully added ${selectedApps.length} apps as favorites for ${this.selectedSubcategory}!`);
          },
          error: (error) => {
            console.error('Error adding apps to favorites:', error);
            this.favoritesLoading = false;
            alert('Failed to add apps to favorites. Please try again.');
          }
        });
      } else {
        // Use old bulk add API for general category browsing
        this.userFavAppService.addMultipleAppsToFavorites(selectedAppIds).subscribe({
          next: (response) => {
            console.log('Successfully added apps to favorites:', response);
            
            const addedCount = response.addedApps ? response.addedApps.length : selectedAppIds.length;
            
            // Update the UI immediately - mark selected apps as favorites
            this.appsInSelectedCategory.forEach(app => {
              if (selectedAppIds.includes(app.appliId)) {
                app.isFavorite = true;
              }
            });
            
            // Clear selections
            this.selectedApps = {};
            
            // Refresh favorite apps list
            this.loadUserFavoriteApps();
            
            this.favoritesLoading = false;
            alert(`Successfully added ${addedCount} apps to favorites!`);
          },
          error: (error) => {
            console.error('Error adding apps to favorites:', error);
            this.favoritesLoading = false;
            alert('Failed to add apps to favorites. Please try again.');
          }
        });
      }
    }).catch(error => {
      console.error('Auth error:', error);
      this.favoritesLoading = false;
      alert('Authentication error. Please log in again.');
    });
  }
  
  // Check which apps are already in favorites
  checkFavoriteApps(): void {
    // Use already loaded favorite apps instead of making another API call
    if (this.userFavoriteApps.length > 0) {
      this.updateFavoriteStatus();
    } else {
      // If favorite apps haven't been loaded yet, load them
      this.loadUserFavoriteApps();
    }
  }
  
  // Get unique subcategory values from current apps
  getUniqueSubCategories(): string[] {
    return [...new Set(this.allAppsInCategory.map(app => app.subCategory || 'Unknown'))];
  }
  
  // Get app count for a specific subcategory
  getAppCountForSubcategory(subcategoryName: string): number {
    return this.allAppsInCategory.filter(app => 
      app.subCategory === subcategoryName || 
      this.getDisplaySubcategoryName(app) === subcategoryName
    ).length;
  }
  
  // Check if an app is in favorites
  isInFavorites(appliId: string): boolean {
    return this.userFavoriteApps.some(app => app.appliId === appliId);
  }
  
  // Remove app from favorites (from category list)
  removeFromFavorites(app: AppInCategory, event: Event): void {
    event.stopPropagation(); // Prevent toggling selection
    
    this.favoritesLoading = true;
    this.userFavAppService.removeFromFavorites(app.appliId).subscribe({
      next: () => {
        console.log(`Successfully removed ${app.name} from favorites`);
        
        // Update app status immediately
        app.isFavorite = false;
        
        // Remove from favorites list
        this.userFavoriteApps = this.userFavoriteApps.filter(a => a.appliId !== app.appliId);
        
        this.favoritesLoading = false;
        alert(`Removed ${app.name} from favorites`);
      },
      error: (error) => {
        console.error('Error removing app from favorites:', error);
        this.favoritesLoading = false;
        alert(`Failed to remove ${app.name} from favorites`);
      }
    });
  }
  
  // Remove app from favorites (from favorites list)
  removeFromFavoritesList(app: UserFavApp): void {
    this.favoritesLoading = true;
    this.userFavAppService.removeFromFavorites(app.appliId).subscribe({
      next: () => {
        this.favoritesLoading = false;
        alert(`Removed ${app.name} from favorites`);
        // Remove from favorites list
        this.userFavoriteApps = this.userFavoriteApps.filter(a => a.appliId !== app.appliId);
        
        // Update favorite status for apps in current category
        this.updateFavoriteStatus();
      },
      error: (error) => {
        console.error('Error removing app from favorites:', error);
        this.favoritesLoading = false;
        alert(`Failed to remove ${app.name} from favorites`);
      }
    });
  }
  
  // Select category
  selectCategory(category: CategoryWithApps): void {
    this.category = category.name;
    this.selectedCategoryId = category.categoryId;
    this.appsInSelectedCategory = category.apps || [];
    this.checkFavoriteApps();
  }
  
  // Open all apps in new tabs
  openAllApps(): void {
    if (this.appsInSelectedCategory.length === 0) {
      alert('No apps available to open');
      return;
    }
    
    // Confirm before opening many tabs
    if (this.appsInSelectedCategory.length > 10) {
      const confirmed = confirm(`This will open ${this.appsInSelectedCategory.length} tabs. Continue?`);
      if (!confirmed) {
        return;
      }
    }
    
    // Open each app in a new tab
    this.appsInSelectedCategory.forEach((app, index) => {
      const url = app.link || app.androidImageUrl;
      if (url) {
        // Small delay between opening tabs to prevent browser blocking
        setTimeout(() => {
          window.open(url, '_blank');
        }, index * 100);
      }
    });
  }
}
